import dayjs from 'dayjs';
import uniqWith from 'lodash/uniqWith';

import type { FeedEntitlementRow, InventoryFeedRow } from '../entitlements-types';

interface ObjectRestoreSelection {
  id: string;
  from: string;
  to: string;
  path: string;
  lifetime: number;
}

interface BuildObjectRestoreSelectionArgs {
  selectedRowIds: string[];
  draftEntitlements: FeedEntitlementRow[];
  feedInventory: InventoryFeedRow[];
  serviceId: number;
  serviceToPathPrefix: Record<number, string>;
}

export function buildObjectRestoreSelection(args: BuildObjectRestoreSelectionArgs): ObjectRestoreSelection[] {
  const { selectedRowIds, draftEntitlements, feedInventory, serviceId, serviceToPathPrefix } = args;

  const selectedEntitlements = selectedRowIds.reduce<ObjectRestoreSelection[]>((all, selectedId) => {
    const activeEntitlement = draftEntitlements.find((entitlement) => entitlement.id === selectedId);
    if (!activeEntitlement) {
      return all;
    }

    const feed = feedInventory.find((feedObject) => feedObject.id === activeEntitlement.feedId);
    const { id, isOngoing, onGoingMaxDate, name, isDayEntitlement } = activeEntitlement;
    let { startDate: from, endDate: to } = activeEntitlement;

    from = isDayEntitlement ? dayjs(from).format('YYYY-MM-DD') : dayjs(from).startOf('month').format('YYYY-MM-DD');
    to = isDayEntitlement
      ? dayjs(to).format('YYYY-MM-DD')
      : dayjs(isOngoing ? onGoingMaxDate ?? new Date() : to)
          .endOf('month')
          .format('YYYY-MM-DD');

    const restoreObject = { id, from, to, lifetime: 15 };
    if (feed?.paths?.length) {
      return [...all, ...feed.paths.map((path, index) => ({ ...restoreObject, id: `${id}-${index}`, path }))];
    }

    const pathPrefix = serviceToPathPrefix[serviceId];
    if (!pathPrefix) {
      return all;
    }

    return [...all, { ...restoreObject, path: `${pathPrefix}${name}` }];
  }, []);

  return uniqWith(selectedEntitlements, (a, b) => a.path === b.path && a.from === b.from && a.to === b.to);
}
